/**
 * Player-side hierarchical correlation source.
 *
 * A player client never owns a `machineId` (that is host-only), but once it is
 * matched to a host it learns the host's `broadcastId`. This module is the
 * single place that tracks that ID and exposes the canonical "stamp outbound
 * message" helper so every WebSocket sender propagates correlation + the active
 * Sentry trace identically. It also mirrors the broadcast_id onto the Sentry
 * scope so player-side issues/logs carry it.
 */

import * as Sentry from '@sentry/react';
import {
  attachObservabilityToMessage,
  type CorrelatableMessage,
  type CorrelationIds,
} from '@playcastdotio/Observability';

let playerBroadcastId: string | undefined;

/**
 * Record the broadcast (host) this player is bound to and tag the Sentry scope.
 * Pass a falsy value to clear it (e.g. on disconnect / requeue).
 */
export function setPlayerBroadcastId(broadcastId: string | undefined): void {
  playerBroadcastId = broadcastId || undefined;
  Sentry.getCurrentScope().setTag('broadcast_id', playerBroadcastId);
}

/** The correlation IDs a player knows at send time. */
export function getPlayerCorrelationIds(): CorrelationIds {
  return { broadcastId: playerBroadcastId };
}

/**
 * Canonical chokepoint helper: stamp the player's correlation IDs and the
 * active Sentry trace onto an outbound WebSocket message. Mutates in place.
 */
export function stampOutbound<T extends CorrelatableMessage>(message: T): T {
  return attachObservabilityToMessage(message, getPlayerCorrelationIds());
}
